import { css } from '@emotion/react';

/**
 * UT Matrix 비교 분석 화면(기계/전기)이 함께 쓰는 스타일 조각.
 *
 * 두 화면은 각자의 emotion styled 루트(`Root` / `UtComparisonElecRoot`)로 스코핑돼 있어
 * 한쪽에 쓴 CSS가 다른 쪽에 닿지 않는다. 그래서 공통 부분은 여기서 css 조각으로 만들어
 * 양쪽 루트에 각각 주입한다.
 *
 * ⚠ 전기 루트는 원래 CSS 변수 없이 hex를 하드코딩하고 있었다. 아래 토큰을 주입해야
 *   공용 SectionHeader(var(--primary) 등에 의존)가 전기 화면에서도 제 색으로 나온다.
 */

/* LGES Design Guideline v1.3.0 토큰 — 상세: D:\AI\LGES_STYLE_GUIDE.md */
export const utTokens = css`
  --bg: #f7f9f8; /* Common-Bg-Deep (Grey-50) */
  --surface: #ffffff; /* Common-Bg-Basic */
  --surface2: #f7f9f8; /* Grey-50 (Table-Bg-Cell-Odd) */
  --border: #dde0df; /* Common-Border-Basic (Grey-300) */
  --primary: #2d9bb2; /* Primary-600 (Key, Infinite Tiffany-Deep) — 강조 텍스트/아이콘용 */
  --primary-light: #1f7795; /* Primary-700 (hover) */
  --btn-primary: #1f7795; /* 레거시 Primary 버튼 기본 (Primary-700) */
  --btn-primary-hover: #3ec2cf; /* 레거시 Primary 버튼 hover (Primary-500) */
  --btn-primary-active: #135678; /* 레거시 Primary 버튼 active (Primary-800) */
  --selected-bg: #eeeaf8; /* 레거시 선택/포커스 배경 (Secondary-50) */
  --selected-border: #a897df; /* 레거시 선택 보더 (Secondary-300) */
  --accent: #554596; /* Secondary-500 (Key, Inspiration Violet) */
  --success: #00806a; /* Status-Confirmed-500 */
  --warning: #ff9322; /* Status-Warning-Major-500 */
  --danger: #f94b50; /* Status-Error-500 */
  --text: #1f1f1f; /* PI 표준 기본 텍스트 (ES Black) */
  --text-muted: #5b5c5b; /* Common-Text-Light (Grey-700) */
  --text-light: #979998; /* Common-Text-Lighter (Grey-500) */
  --radius: 2px; /* PI 표준 (버튼 md/sm, 메뉴) */
  --radius-lg: 3px; /* PI 표준 (버튼 lg) */
  --border-input: #b9bcbb; /* PI 입력요소 보더 (Grey-400) */
  --shadow: 0 1px 4px rgba(31, 31, 31, 0.08); /* Elevation Depth 1 */
  --shadow-md: 0 4px 12px rgba(31, 31, 31, 0.12); /* Elevation Depth 2 */
`;

/**
 * 섹션 머리 + 본문 (components/SectionBox · SectionHeader가 그리는 마크업).
 *
 * 테두리 있는 상자가 아니라 "파란 제목 + 가로선"으로 영역을 구분한다.
 */
export const utSectionStyles = css`
  .section-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--primary);
    margin: 24px 0 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .section-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
  /*
    제목줄 우측 액션 묶음 — 위 ::after(가로선)가 order 0 무리의 맨 뒤라,
    이 묶음만 order: 1로 보내면 "제목 → 선 → 버튼" 순서가 된다.
    덕분에 가로선을 마크업에서 별도 요소로 꺼내지 않아도 된다.
  */
  .section-title .section-actions {
    order: 1;
    flex-shrink: 0;
  }
  /* 제목은 700/primary라 건수·보조문구는 본문 톤으로 낮춘다 */
  .section-title .section-count,
  .section-title .section-note {
    font-size: 12px;
    font-weight: 400;
    color: var(--text-muted);
  }
  /* 건수 강조는 총 건수·보조 수치 모두 같은 톤 */
  .section-title .section-count strong,
  .section-title .section-note strong {
    color: var(--primary);
    font-weight: 700;
    padding: 0 2px;
  }
  /* 눈에 띄어야 하는 수치(차이 건수 등)만 빨간색 — 쓰는 쪽에서 strong에 붙인다 */
  .section-title .section-note strong.danger {
    color: var(--danger);
  }
  /* 감소 수치 — 증가(danger)와 짝으로 쓴다 (⑧ 증가/감소 설비 도표) */
  .section-title .section-note strong.success {
    color: var(--success);
  }
  /* 총 건수 뒤에 보조 문구가 이어지면 그 사이에 구분자를 넣는다 */
  .section-title .section-count + .section-note::before {
    content: '|';
    margin-right: 6px;
    color: var(--text-light);
    font-weight: 400;
  }

  /* 접혔을 때는 본문이 없으니 제목 아래 여백을 없앤다 */
  .section-box.folded > .section-title {
    margin-bottom: 0;
  }

  /* 섹션 본문의 한 줄짜리 안내/오류 문구 */
  .hint-text {
    font-size: 12px;
    color: var(--text-muted);
  }
  .error-text {
    font-size: 12px;
    color: #b32542; /* Error-700 */
  }
  /* 저장 완료 표시 (저장 버튼 옆) */
  .saved-msg {
    font-size: 12px;
    font-weight: 700;
    color: var(--success);
    margin-right: 8px;
  }

  /*
    CustomChip 한 줄.
    CustomChip 안의 Stack은 기본이 nowrap이라, 항목이 많으면 가로로 밀려 나간다.
  */
  .chip-row {
    margin-bottom: 12px;
  }
  .chip-row .MuiStack-root {
    flex-wrap: wrap;
    row-gap: 4px;
  }

  /*
    헤더 글자를 작게 쓰는 CustomGrid — 감싸는 요소에 이 클래스를 준다.

    공용 그리드 CSS(ui/css/multiSelect.css)는 헤더 셀에 font-size를 따로 주지 않아
    본문 셀과 같은 13px을 물려받는다. UT 비교 분석의 표들은 헤더 라벨이 길고 열이 많아
    예전 <table> 시절(11px)처럼 줄여야 잘리지 않는다.
    다른 화면의 CustomGrid에는 닿지 않도록 래퍼 클래스로 스코프한다.
  */
  .grid-sm-header
    .wj-flexgrid
    div[wj-part='ch']
    div[wj-part='chcells'].wj-colheaders
    .wj-row
    .wj-cell {
    font-size: 11px;
  }
`;
